import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { createProfile, getMyProfile } from '../lib/cortivoxApi';

// Контекст авторизации (GoTrue). Опционален: если Supabase не сконфигурирован
// (нет anon-ключа при сборке), провайдер отдаёт «незалогинен, вход недоступен»,
// и приложение работает как прежде.
//
// «Врач cortivox» — запись в cortivox.profiles (общий пул пользователей GoTrue,
// но профиль свой). Профиль читается и создаётся только через cortivox-api —
// там правила: email из токена в нижнем регистре, поля не своей роли обнуляются.
const AuthContext = createContext({
  session: null,
  user: null,
  doctor: null,
  loading: true,
  configured: false,
  signIn: async () => ({ error: new Error('Auth is not configured') }),
  signUp: async () => ({ error: new Error('Auth is not configured') }),
  signOut: async () => {},
  refreshDoctor: async () => {},
});

export function AuthProvider({ children }) {
  const configured = isSupabaseConfigured();
  const [session, setSession] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(configured);

  const userId = session?.user?.id ?? null;

  // Профиль текущего пользователя (GET /api/profiles/me).
  const loadDoctor = useCallback(async (uid) => {
    if (!configured || !uid) {
      setDoctor(null);
      return null;
    }
    try {
      const profile = await getMyProfile();
      setDoctor(profile);
      return profile;
    } catch (err) {
      // 404 — профиля ещё нет, это нормальное состояние. Остальное (API
      // недоступен и т.п.) не роняет приложение: вход опционален.
      if (err?.status !== 404) console.warn('Не удалось загрузить профиль:', err?.message);
      setDoctor(null);
      return null;
    }
  }, [configured]);

  useEffect(() => {
    if (!configured) return;

    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession ?? null);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [configured]);

  // Подгружаем/сбрасываем профиль врача при смене пользователя.
  useEffect(() => {
    loadDoctor(userId);
  }, [userId, loadDoctor]);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      doctor,
      loading,
      configured,

      signIn: async (email, password) => {
        if (!configured) {
          return { error: new Error('Вход недоступен: Supabase не настроен.') };
        }
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return { error };
      },

      // Регистрация врача: пользователь заводится в GoTrue, профиль (role=doctor)
      // создаёт API. MAILER_AUTOCONFIRM=true, поэтому сессия и токен для API
      // появляются сразу после signUp.
      // role: 'doctor' (по умолчанию) или 'patient'. Пациенту нужны дата
      // рождения и годы образования: по ним сервер считает возрастную поправку
      // и поправку на образование в MoCA.
      signUp: async (email, password, { fullName, specialization, role = 'doctor', birthDate, educationYears } = {}) => {
        if (!configured) {
          return { error: new Error('Регистрация недоступна: Supabase не настроен.') };
        }
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) return { error };

        const uid = data.user?.id;
        if (uid) {
          try {
            await createProfile({
              role,
              full_name: fullName || null,
              specialization: role === 'doctor' ? specialization || null : null,
              birth_date: role === 'patient' ? birthDate || null : null,
              education_years: role === 'patient' && educationYears != null ? Number(educationYears) : null,
            });
          } catch (profileError) {
            // Пользователь создан, но профиль не записался — вход это не
            // блокирует, но сообщаем.
            return { error: null, warning: profileError.message };
          }
          await loadDoctor(uid);
        }
        return { error: null };
      },

      signOut: async () => {
        if (configured) await supabase.auth.signOut();
        setDoctor(null);
      },

      refreshDoctor: () => loadDoctor(userId),
    }),
    [session, doctor, loading, configured, loadDoctor, userId]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
